import React, { useMemo } from 'react';
import { Button, Skeleton, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import useLatestMeasurementSummaries, { LatestMeasurementSummary } from '../../hooks/useLatestMeasurementSummaries';
import { getRecencyStatus, RECENCY_STATUS_CONFIG } from '../../helpers/reports/reportStatus';
import { HealthStatus, HEALTH_STATUS_CONFIG } from '../../helpers/reports/areaHealthStatus';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ReportLatestDaySummaryCardProps {
    areaId?: number | null;
    areaName?: string | null;
    areas?: Array<{ id: number; name?: string | null }>;
    onSelectDateRange?: (range: [Dayjs, Dayjs], areaId: number) => void;
    summaries?: LatestMeasurementSummary[];
    loading?: boolean;
    error?: string | null;
    healthStatusByAreaId?: Record<number, HealthStatus>;
}

export const ReportLatestDaySummaryCard: React.FC<ReportLatestDaySummaryCardProps> = ({
    areaId,
    areaName,
    areas,
    onSelectDateRange,
    summaries,
    loading,
    error,
    healthStatusByAreaId,
}) => {
    const targetAreas = useMemo(() => {
        if (areas && areas.length > 0) {
            return areas.filter(a => typeof a.id === 'number');
        }
        if (areaId) {
            return [{ id: areaId, name: areaName }];
        }
        return [];
    }, [areas, areaId, areaName]);

    const shouldUseHook = summaries === undefined && loading === undefined && error === undefined;
    const hookState = useLatestMeasurementSummaries(targetAreas, { enabled: shouldUseHook });

    const resolvedSummaries = summaries ?? hookState.summaries;
    const resolvedLoading = loading ?? hookState.loading;
    const resolvedError = error ?? hookState.error;

    const title = areaName ? `Latest data in ${areaName}` : 'Latest data in area';
    const hasMultiple = targetAreas.length > 1;

    return (
        <div className="reports-latest-surface">
            <div className="reports-latest-surface-head">
                <div className="reports-latest-surface-title">{title}</div>
                <div className="reports-latest-surface-meta">
                    {resolvedSummaries.length > 0
                        ? `${resolvedSummaries.length} ${resolvedSummaries.length === 1 ? 'area' : 'areas'}`
                        : 'Latest scan status'}
                </div>
            </div>

            {resolvedLoading ? (
                <div className="reports-latest-loading">
                    <Skeleton active paragraph={{ rows: 2 }} />
                </div>
            ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {resolvedError && <Text type="danger">{resolvedError}</Text>}
                    {!resolvedError && resolvedSummaries.length === 0 && (
                        <div className="reports-latest-empty">
                            <Text type="secondary">No measurements available for this area yet.</Text>
                        </div>
                    )}
                    {!resolvedError && resolvedSummaries.length > 0 && (
                        <div className="reports-latest-grid">
                            {resolvedSummaries.map((summary) => {
                                const hasData = !!summary.range && summary.count > 0;
                                const recencyStatus = getRecencyStatus(summary.latestAt);
                                const recencyMeta = RECENCY_STATUS_CONFIG[recencyStatus];
                                const healthStatus = healthStatusByAreaId?.[summary.areaId] ?? 'healthy';
                                const healthMeta = HEALTH_STATUS_CONFIG[healthStatus];
                                const daysSince = summary.latestAt ? dayjs().diff(summary.latestAt, 'day') : null;
                                const displayName = hasMultiple
                                    ? summary.areaName || `Area ${summary.areaId}`
                                    : summary.areaName || areaName || `Area ${summary.areaId}`;

                                return (
                                    <div
                                        key={`latest-day-${summary.areaId}`}
                                        className={`reports-latest-entry reports-latest-entry--health-${healthStatus}`}
                                    >
                                        <div className="reports-latest-header">
                                            <div>
                                                <Text strong>{displayName}</Text>
                                                <div className="reports-latest-status">
                                                    <span>{healthMeta.label}</span>
                                                    {recencyStatus !== 'recent' && recencyStatus !== 'no-data' && (
                                                        <span className="reports-latest-warning">
                                                            <ExclamationCircleOutlined />
                                                            {recencyMeta.label}
                                                        </span>
                                                    )}
                                                    {daysSince !== null && recencyStatus !== 'no-data' && (
                                                        <span className="reports-latest-age">{daysSince}d ago</span>
                                                    )}
                                                </div>
                                                {summary.error ? (
                                                    <Text type="danger" style={{ display: 'block' }}>
                                                        {summary.error}
                                                    </Text>
                                                ) : null}
                                            </div>
                                            <Button
                                                type="primary"
                                                size="middle"
                                                disabled={!hasData || !onSelectDateRange}
                                                onClick={() => {
                                                    if (summary.range && onSelectDateRange) {
                                                        onSelectDateRange(summary.range, summary.areaId);
                                                    }
                                                }}
                                            >
                                                View report
                                            </Button>
                                        </div>
                                        <div className="reports-latest-metrics">
                                            <div>
                                                <Text type="secondary">Last scan</Text>
                                                <Text strong>
                                                    {summary.latestAt ? summary.latestAt.format('MMM D, YYYY') : 'No scans yet'}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Measurements</Text>
                                                <Text strong>{summary.count}</Text>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Space>
            )}
        </div>
    );
};

export default ReportLatestDaySummaryCard;
